import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Box, Button, Stack, styled, TableCell, TableRow } from '@mui/material';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import IosSwitch from '../../Components/IOSSwitch';
import { TypoPoppins } from '../../Components/Typo';

import { ORDER_STATUS } from '../../Constant/Enums';

import ToastContext from '../../Context/ToastContext';

import { toCapitalize } from '../../Helpers';

import { FindUserOrders, UsersQuery } from '../../Server/Query';
import {
  UpdateEventBookerActiveState,
  UpdateOrderStatus,
} from '../../Server/Mutation';

import CustomTable from './Components/CustomTable';
import SearchField from './Components/SearchField';
import ConfirmationModal from './Components/ConfirmationModal';

const Client = () => {
  const { showToastMessage } = useContext(ToastContext);

  const [search, setSearch] = useState('');
  const [eventBookers, setEventBookers] = useState([]);
  const [clientJobRequests, setClientJobRequests] = useState([]);
  const [selectedJobRequestID, setSelectedJobRequestID] = useState('');
  const [isActiveConfirmModalOpen, setIsActiveConfirmModalOpen] =
    useState(false);

  const [pendingActiveStateChangeData, setPendingActiveStateChangeData] =
    useState({
      eventBookerId: null,
      isActive: null,
    });

  const filteredEventBookers = useMemo(() => {
    if (search === '') return eventBookers;

    return eventBookers.filter(eventBooker => {
      const isMatchedByName = eventBooker.name
        .toLowerCase()
        .startsWith(search.toLowerCase());

      const isMatchedById = eventBooker.eventBookerId
        .toLowerCase()
        .startsWith(search.toLowerCase());

      return isMatchedByName || isMatchedById;
    });
  }, [eventBookers, search]);

  useEffect(() => {
    Axios(UsersQuery)
      .then(({ data, errors }) => {
        if (errors) {
          showToastMessage('Something went wrong, Unable to load data');
          return undefined;
        }

        const eventBookers = (data?.EventBookers ?? []).map(parseEventBooker);
        setEventBookers(eventBookers);
      })
      .catch(() => showToastMessage('Something went wrong, Unable to load data'));
  }, [showToastMessage]);

  function handleChangeSearch(event) {
    setSearch(event.target.value);
  }

  async function handleClickClientRecord(userId) {
    setSelectedJobRequestID('');
    const query = FindUserOrders({ id: userId });
    const { data, errors } = await Axios(query);

    if (errors) {
      showToastMessage(
        'Something went wrong, Failed to load order information'
      );
      return undefined;
    }

    const orders = (data?.Orders ?? []).map(parseOrder);
    setClientJobRequests(orders);
  }

  async function handleClickReject() {
    try {
      const { errors } = await Axios(UpdateOrderStatus({
          orderStatus: ORDER_STATUS.REJECTED,
          orderUniqueID: selectedJobRequestID,
        }));

      if (errors) {
        showToastMessage('Something went wrong');
        return undefined;
      }

      showToastMessage('Job request rejected successfully', 'success');
      setClientJobRequests([]);
      setSelectedJobRequestID('');
    } catch {
      // network/HTTP failure is already surfaced via the global API error toast
    }
  }

  function handleChangeIsActive(event, eventBookerId) {
    setPendingActiveStateChangeData({
      isActive: event.target.checked,
      eventBookerId: eventBookerId,
    });
    setIsActiveConfirmModalOpen(true);
  }

  async function toggleActiveState() {
    const { eventBookerId, isActive } = pendingActiveStateChangeData;
    try {
      const query = UpdateEventBookerActiveState({ id: eventBookerId, isActive });
      const { errors } = await Axios(query);
      if (errors) {
        setIsActiveConfirmModalOpen(false);
        showToastMessage('Something went wrong, Failed to update Event Booker.');
        return undefined;
      }

      const eventBookersClone = JSON.parse(JSON.stringify(eventBookers));
      eventBookersClone.forEach(eventBooker => {
        if (eventBooker.id === eventBookerId) eventBooker.isActive = isActive;
      });
      setEventBookers(eventBookersClone);
      setIsActiveConfirmModalOpen(false);
    } catch {
      // network/HTTP failure is already surfaced via the global API error toast
      setIsActiveConfirmModalOpen(false);
    }
  }

  return (
    <>
      <Box my={{ xs: 2.5, md: 5 }} px={{ xs: 2.5, md: 5 }}>
        <Stack direction='row' mb={3}>
          <SearchField value={search} onChange={handleChangeSearch} />
        </Stack>
        <TypoPoppins variant='body1' mb={1}>
          Client Name
        </TypoPoppins>
        <CustomTable
          height='300px'
          tableHeadings={eventBookerTableColumns}
          sx={{ overflowY: 'auto' }}
        >
          {filteredEventBookers.map(row => (
            <TableRow
              key={row.id}
              hover
              onClick={() => handleClickClientRecord(row.id)}
            >
              <TableCell>{row.no}</TableCell>
              <TableCell>{row.eventBookerId}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.address}</TableCell>
              <TableCell>{row.province}</TableCell>
              <TableCell>{row.postCode}</TableCell>
              <TableCell>{row.phoneNumber}</TableCell>
              <TableCell>
                <IosSwitch disabled checked={row.isSubscribed} />
              </TableCell>
              <TableCell>
                <IosSwitch
                  checked={row.isActive}
                  onChange={event => handleChangeIsActive(event, row.id)}
                />
              </TableCell>
            </TableRow>
          ))}
        </CustomTable>
        <RejectButton
          color='red3'
          disabled={!selectedJobRequestID}
          onClick={handleClickReject}
        >
          Reject
        </RejectButton>
        <TypoPoppins variant='body1' mb={1}>
          Job Requests
        </TypoPoppins>
        <CustomTable
          tableHeadings={[
            'No.',
            'Order ID',
            'Prof. Full name',
            'Work',
            'Date',
            'Time',
            'State',
            'Event Date',
          ]}
          height='300px'
          sx={{ overflowY: 'auto' }}
        >
          {clientJobRequests.map(row => (
            <TableRow
              key={row.id}
              hover
              onClick={() => setSelectedJobRequestID(row.id)}
            >
              <TableCell>{row.no}</TableCell>
              <TableCell>{row.orderId}</TableCell>
              <TableCell>{row.profFullName}</TableCell>
              <TableCell>{row.work}</TableCell>
              <TableCell>{row.createdDate}</TableCell>
              <TableCell>{row.createdTime}</TableCell>
              <TableCell sx={{ textTransform: 'capitalize' }}>
                {row.state}
              </TableCell>
              <TableCell>{row.eventDate}</TableCell>
            </TableRow>
          ))}
        </CustomTable>
      </Box>

      <ConfirmationModal
        open={isActiveConfirmModalOpen}
        onClose={() => setIsActiveConfirmModalOpen(false)}
        onClickConfirm={toggleActiveState}
        content={{
          title: `${
            pendingActiveStateChangeData.isActive ? 'Unblock' : 'Block'
          } user`,
          description: `Are you sure want to ${
            pendingActiveStateChangeData.isActive ? 'unblock' : 'block'
          } this Event booker`,
        }}
      />
    </>
  );
};

const eventBookerTableColumns = [
  'No.',
  'Client ID',
  'Full name',
  'Email',
  'Address',
  'Province',
  'Post Code',
  'Phone number',
  'Subscribed',
  'Active',
];

const RejectButton = styled(Button)({
  color: 'white',
  display: 'table',
  marginLeft: 'auto',
  marginBlock: 16,
  paddingInline: 32,
});

function parseEventBooker(eventBooker, index) {
  const name = [eventBooker.first_name, eventBooker.last_name].filter(Boolean).join(' ');
  const no = (index + 1).toString().padStart(2, '0');

  return {
    no,
    name,
    id: eventBooker.id,
    eventBookerId: eventBooker.event_booker_id,
    email: eventBooker.email,
    province: eventBooker.province,
    postCode: eventBooker.postal_code,
    address: eventBooker.address_line_1,
    phoneNumber: eventBooker.mobile_number,
    isSubscribed: eventBooker.is_subscribed,
    isActive: eventBooker.is_active,
  };
}

function parseOrder(order, index) {
  const service = order.Service ?? {};
  const professional = service.Professional ?? {};
  const createdAt = order.created_at ? new Date(order.created_at) : null;
  const eventDate = order.date ? new Date(order.date) : null;

  return {
    id: order.id,
    orderId: order.orderRef ?? order.order_id ?? order.id,
    no: (index + 1).toString().padStart(2, '0'),
    profFullName: [professional.first_name, professional.last_name].filter(Boolean).join(' ') || 'Not available',
    work: service.name ? toCapitalize(service.name) : 'Not available',
    createdDate: createdAt ? createdAt.toLocaleDateString() : '',
    createdTime: createdAt
      ? createdAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : '',
    state: toCapitalize(order.status ?? ''),
    eventDate: eventDate ? eventDate.toLocaleDateString() : '',
  };
}

export default Client;
